// frontend/src/pages/usecases/Research.jsx
// Use-case landing page: Research session management

import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeContext';
import { Link } from 'react-router-dom';

const CHROME_URL =
  'https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc';

const STEPS = [
  {
    num: '1',
    title: 'Save your entire research window',
    desc: 'Papers, docs, Stack Overflow threads, GitHub repos — every tab captured with one click.',
  },
  {
    num: '2',
    title: 'Close your tabs and work on something else',
    desc: 'Free up your browser without losing hours of curated context.',
  },
  {
    num: '3',
    title: 'Come back tomorrow and resume with one click',
    desc: 'Restore the full session on any device or browser. Pick up exactly where you left off.',
  },
];

const Research = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { darkMode, toggleDarkMode } = useThemeMode();

  return (
    <>
      <Helmet>
        <title>Save a Research Session Across 40 Tabs — Pick Up Where You Left Off | Blackmount</title>
        <meta
          name="description"
          content="You're deep in research — papers, docs, Stack Overflow, GitHub repos. Save your entire session with one click and resume later on any device."
        />
        <link rel="canonical" href="https://app.blackmount.ai/use-cases/research" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Save a Research Session Across 40 Tabs | Blackmount" />
        <meta property="og:description" content="Save your entire research session — papers, docs, repos — and resume on any device with one click." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.blackmount.ai/use-cases/research" />
        <meta property="og:image" content="https://app.blackmount.ai/og-image.png" />
        <meta property="og:site_name" content="Blackmount" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Save a Research Session Across 40 Tabs | Blackmount" />
        <meta name="twitter:description" content="Save your entire research session — papers, docs, repos — and resume on any device with one click." />
        <meta name="twitter:image" content="https://app.blackmount.ai/og-image.png" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Top Bar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Box
                component={Link}
                to="/"
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <Box
                  component="img"
                  src="/blackmount-logo.jpg"
                  alt="Blackmount"
                  sx={{ height: 32, width: 'auto', borderRadius: 0.5 }}
                />
              </Box>
            </Box>
            <IconButton color="inherit" onClick={toggleDarkMode}>
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Hero */}
        <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            component="h1"
            fontWeight={700}
            sx={{ mb: 3, color: 'text.primary' }}
          >
            Save a Research Session Across 40 Tabs
          </Typography>

          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 620, mx: 'auto', lineHeight: 1.6 }}
          >
            You're deep in research — papers, docs, Stack Overflow, GitHub repos. You need to
            switch tasks. If you close those tabs, hours of context vanish.
          </Typography>

          {/* Screenshot placeholder */}
          <Box
            sx={{
              mx: 'auto',
              mb: 8,
              maxWidth: 600,
              height: 260,
              borderRadius: 3,
              border: `2px dashed ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.action.hover,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Screenshot coming soon
            </Typography>
          </Box>

          {/* How it works */}
          <Typography variant="h5" fontWeight={600} sx={{ mb: 4, color: 'text.primary' }}>
            How it works
          </Typography>

          <Stack spacing={4} sx={{ maxWidth: 520, mx: 'auto', textAlign: 'left', mb: 8 }}>
            {STEPS.map((step) => (
              <Box key={step.num} sx={{ display: 'flex', gap: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    flexShrink: 0,
                  }}
                >
                  {step.num}
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          {/* CTA */}
          <Button
            variant="contained"
            size="large"
            startIcon={<ExtensionIcon />}
            onClick={() => window.open(CHROME_URL, '_blank', 'noopener,noreferrer')}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 3,
              textTransform: 'none',
            }}
          >
            Add to Chrome — Free
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default Research;
